import { useEffect, useState } from "react";
import { BellOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getNativeBridge } from "@/lib/nativeBridge";

const DISMISS_KEY = "courtvibe_push_blocked_dismissed";

/**
 * Shown only inside the native shell, when the OS-level notification
 * permission is blocked but the user still has notifications enabled in
 * the app. The CTA opens the OS settings page for this app via the
 * native bridge so they can re-enable push.
 */
export function NotificationBlockedBanner() {
  const [blocked, setBlocked] = useState<boolean>(
    () => getNativeBridge()?.pushBlocked === true,
  );
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onChange = () => {
      const next = getNativeBridge()?.pushBlocked === true;
      setBlocked(next);
      // Re-open the banner if the OS state flips back to blocked after
      // a previous dismiss — the user clearly cares now.
      if (next) {
        try {
          sessionStorage.removeItem(DISMISS_KEY);
        } catch {
          // ignore
        }
        setDismissed(false);
      }
    };
    window.addEventListener("courtvibe:push-blocked-changed", onChange);
    return () => {
      window.removeEventListener("courtvibe:push-blocked-changed", onChange);
    };
  }, []);

  if (!blocked || dismissed) return null;

  const handleOpenSettings = () => {
    getNativeBridge()?.openOSSettings?.();
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="sticky top-0 z-50 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900"
      data-testid="banner-push-blocked"
    >
      <div className="flex items-center gap-3 px-4 py-2">
        <BellOff className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
        <p className="flex-1 text-sm text-amber-900 dark:text-amber-100">
          Las notificaciones están desactivadas en el sistema. Actívalas para
          recibir avisos de partidos y ligas.
        </p>
        <Button
          size="sm"
          variant="default"
          onClick={handleOpenSettings}
          data-testid="button-open-os-settings"
        >
          Abrir ajustes
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleDismiss}
          data-testid="button-dismiss-push-blocked"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
